<template>
  <rbz-modal-dialog
    v-if="showEditor"
    :show="true"
    size="full"
    @close-modal="closeModal"
    :hide-close-icon="true"
    :hide-header="true"
    :hide-footer="true"
    v-bind="$attrs"
  >
    <template #header>
      <div class="modal-header-wrapper">
        <div class="editor-modal-header">
          <div class="editor-modal-title" data-qa="editor-modal-title">
            {{ title }}
          </div>
          <rbz-form-button
            variant="tertiary"
            icon="IconClose"
            :icon-props="{ color: 'dusty-gray' }"
            @button-click="closeModal()"
            :ignore-update-in-process="true"
            class="close-modal-button"
            data-qa="close-modal-button"
          />
        </div>
        <layout-divider-line color="gray" class="rbz-page-header-divider" />
      </div>
    </template>
    <template #content>
      <div class="editor-modal-content">
        <component :is="editorView" :read-only="true" />
      </div>
    </template>
  </rbz-modal-dialog>
</template>

<script setup lang="ts">
import { defineAsyncComponent, markRaw, ref, watch } from 'vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import RbzFormButton from '@/components/RbzFormButton.vue';
import { useTagsStore } from '@/store/TagsStore';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';

const tagsStore = useTagsStore();
const editorView = ref(null);
const showEditor = ref(false);
const title = ref('');

watch([() => tagsStore.editorDocId, () => tagsStore.editorDocType], (newValues, _oldValues) => {
  const newEditorDocId = newValues[0];
  const newEditorDocType = newValues[1];
  if (newEditorDocId && newEditorDocType) {
    loadEditorView();
  } else {
    editorView.value = null;
    showEditor.value = false;
  }
});

const closeModal = () => {
  tagsStore.setEditorDocId(null);
  tagsStore.setEditorDocType(null);
  editorView.value = null;
  title.value = '';
  showEditor.value = false;
};

const docTypeToComponentName = (docType: string): string | null => {
  if (!docType) return null;
  const parts = docType.split('-');
  const capitalizedParts = parts.map((part) => part.charAt(0).toUpperCase() + part.slice(1));
  title.value = capitalizedParts.join(' ') + ' Viewer';
  if (title.value === 'Acl Profiles Viewer') {
    title.value = 'ACL Profiles Viewer';
  }
  // Return only the base name (without 'Editor') so the suffix can be kept
  // as a literal string in the dynamic import below — this makes Rollup's
  // static analysis match only *Editor.vue files instead of every view.
  return capitalizedParts.join('');
};

const loadEditorView = async () => {
  const editorDocType = tagsStore.editorDocType;
  if (!editorDocType) {
    console.error('Editor document type is missing.');
    editorView.value = null;
    showEditor.value = false;
    return;
  }

  const editorBase = docTypeToComponentName(editorDocType);
  if (!editorBase) {
    console.error('Invalid document type:', editorDocType);
    editorView.value = null;
    return;
  }
  // 'Editor' is kept as a literal suffix so Rollup's glob expansion covers only
  // *Editor.vue files — preventing INEFFECTIVE_DYNAMIC_IMPORT warnings caused by
  // accidentally matching views like MainComponent.vue that are also statically
  // imported elsewhere.
  const editorComponent = `${editorBase}Editor`;
  try {
    editorView.value = markRaw(
      defineAsyncComponent({
        loader: () => import(`@/views/${editorBase}Editor.vue`),
        onError: (error) => {
          console.error(`Failed to load editor component ${editorComponent}:`, error);
          editorView.value = null;
        },
      })
    );
    showEditor.value = true;
  } catch (e) {
    console.error(`Error setting up async component ${editorComponent}:`, e);
    editorView.value = null;
  }
};
</script>

<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.modal-header-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 10px 40px;

  .editor-modal-header {
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: 10px 0;

    .editor-modal-title {
      color: map.get(colors.$colors, 'black');
      font-size: 24px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px;
      align-self: center;
    }
  }

  .divider-line {
    margin-top: 10px;
  }

  .close-modal-button {
    :deep(.rbz-button) {
      border: 0;
      height: 24px;
      padding: 0;
      width: 24px;
    }
  }
}

.editor-modal-content {
  :deep(.rbz-page-header) {
    display: none;
  }

  :deep(.rbz-page-content) {
    padding: 15px 32px 32px;
  }

  :deep(.rbz-label) {
    color: map.get(colors.$colors, 'bright-gray');

    &.disabled {
      @extend :deep(.rbz-label);
    }
  }

  :deep(.tags-columns.rbz-columns.faded-segment) {
    opacity: 1;
    pointer-events: none;
  }

  :deep(.tags-list-button, .expand-icon-wrapper, .clear-icon-wrapper) {
    display: none;
  }

  :deep(.fill.disabled) {
    color: map.get(colors.$colors, 'fun-blue');
    background-color: map.get(colors.$colors, 'hawkes-blue');
  }

  :deep(.button-toggle.disabled),
  :deep(.automatic-tags),
  :deep(.excluded-tags-cell),
  :deep(.tag-display) {
    opacity: 0.5;
    pointer-events: none;
  }

  :deep(.dropdown-header.disabled),
  :deep(.dropdown-holder .dropdown-placeholder) {
    opacity: 0.5;
    color: map.get(colors.$colors, 'shuttle-gray');
  }

  :deep(.textarea-holder.disabled .form-textarea),
  :deep(.input-holder.disabled .form-input) {
    color: map.get(colors.$colors, 'shuttle-gray');
    border: 1px solid map.get(colors.$colors, 'silver');
    opacity: 0.5;
  }

  :deep(.checkbox-holder .checkbox-style:disabled:checked) {
    background-color: map.get(colors.$colors, 'jewel');
    border: 2px solid transparent;
    outline: none;
  }

  :deep(.rbz-card-content) {
    pointer-events: none;
  }
}
</style>
